/**
 * Enrich the AI-extracted attributes with ALL available metadata from the
 * input row stored in IndexedDB.  This ensures that manufacturer names,
 * part numbers, vendor info, AND URLs all make it into the chatgptOutput
 * JSON that is ultimately sent to the backend for DB save.
 *
 * Existing keys in `attrs` are never overwritten — the function only fills
 * missing fields.
 *
 * @param {Object} attrs     - Extracted attributes (what the AI returned)
 * @param {Object} rowData   - The full item object from IndexedDB
 * @returns {Object}           A *new* object with all available metadata merged in
 */

/**
 * Helper: Extract a human-readable company name from a URL domain.
 * e.g. "https://www.motion.com/products/..." → "Motion"
 *      "https://www.grainger.com/..."         → "Grainger"
 */
function extractNameFromUrl(url) {
    if (!url) return '';
    try {
        const hostname = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
        // Take first segment of domain: "motion.com" → "motion", "mcmaster.com" → "mcmaster"
        const parts = hostname.split('.');
        if (parts.length >= 2) {
            const name = parts[parts.length - 2]; // second-to-last part
            // Capitalize first letter
            return name.charAt(0).toUpperCase() + name.slice(1);
        }
        return '';
    } catch {
        return '';
    }
}

/**
 * Helper: Extract manufacturer/vendor data from the processingLog entries.
 * Looks at AI_SELECT and MFR_VALIDATE stages for classification data.
 */
function extractFromProcessingLog(processingLog) {
    const result = { manufacturers: [], vendors: [], manufacturerUrl: '', bestUrl: '' };
    if (!Array.isArray(processingLog)) return result;

    for (const entry of processingLog) {
        if (!entry.data) continue;

        if (entry.stage === 'AI_SELECT' && entry.result === 'success') {
            // AI_SELECT stores the full classification result
            if (entry.data.manufacturers?.length > 0) {
                result.manufacturers = entry.data.manufacturers;
            }
            if (entry.data.vendors?.length > 0) {
                result.vendors = entry.data.vendors;
            }
            if (entry.data.manufacturerUrl) {
                result.manufacturerUrl = entry.data.manufacturerUrl;
            }
            if (entry.data.bestUrl) {
                result.bestUrl = entry.data.bestUrl;
            }
        }

        if (entry.stage === 'MFR_VALIDATE' && entry.result === 'success' && entry.data) {
            // MFR_VALIDATE stores manufacturer name and result URL
            if (entry.data.manufacturer && !result.manufacturers.some(m => m.name === entry.data.manufacturer)) {
                result.manufacturers.push({
                    name: entry.data.manufacturer,
                    url: entry.data.resultUrl || entry.data.originalUrl || '',
                    partNumber: ''
                });
            }
        }
    }
    return result;
}

export const injectManufacturerMeta = (attrs, rowData = {}) => {
    if (!attrs || typeof attrs !== 'object') return attrs;

    const merged = { ...attrs };

    // Helper: set a key only if it's not already present
    const setIfMissing = (key, value) => {
        if (!value) return;
        const val = String(value).trim();
        if (!val || val === 'null' || val === 'NULL' || val === 'undefined') return;
        // Check both exact key and normalized (lowercased, no spaces) key
        const normKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!merged[key] && !merged[normKey]) {
            merged[key] = val;
        }
    };

    // ── Gather ALL available data sources (with processing log as fallback) ──
    let mfrs = rowData.classifiedManufacturers || [];
    let vdrs = rowData.classifiedVendors || [];
    let logMfrUrl = '';
    let logBestUrl = '';

    // Fallback: If classifiedManufacturers/Vendors are empty, try processing log
    if ((mfrs.length === 0 || vdrs.length === 0) && rowData.processingLog) {
        const logData = extractFromProcessingLog(rowData.processingLog);
        if (mfrs.length === 0 && logData.manufacturers.length > 0) {
            mfrs = logData.manufacturers;
        }
        if (vdrs.length === 0 && logData.vendors.length > 0) {
            vdrs = logData.vendors;
        }
        logMfrUrl = logData.manufacturerUrl || '';
        logBestUrl = logData.bestUrl || '';
    }

    // ── De-duplicate: Remove manufacturer entries that share the same domain as a vendor ──
    if (mfrs.length > 0 && vdrs.length > 0) {
        const getDomain = (url) => { try { return new URL(url).hostname.replace(/^www\./, '').toLowerCase(); } catch { return ''; } };
        const vdrDomains = new Set(vdrs.map(v => getDomain(v.url)).filter(Boolean));
        mfrs = mfrs.filter(m => {
            const mDom = getDomain(m.url);
            return !mDom || !vdrDomains.has(mDom);
        });
        // Also check by name: if manufacturer name matches a vendor name (case-insensitive), remove
        const vdrNames = new Set(vdrs.map(v => (v.name || '').toLowerCase()).filter(Boolean));
        mfrs = mfrs.filter(m => !vdrNames.has((m.name || '').toLowerCase()));
    }

    // ── Manufacturer Part Number 1 ──
    setIfMissing('MANUFACTURER PART NUMBER 1',
        mfrs[0]?.partNumber ||
        rowData.manufacturerNumber ||
        rowData.manufacturerPartNumber ||
        rowData.mfrPartNumber ||
        ''
    );

    // ── Manufacturer Name 1 ──
    const mfr1Name =
        mfrs[0]?.name ||
        rowData.manufacturerName ||
        rowData.mfrName ||
        '';
    setIfMissing('MANUFACTURER NAME 1', mfr1Name);

    // ── Manufacturer URL 1 ──
    const mfrUrl =
        mfrs[0]?.url ||
        rowData.manufacturerUrl ||
        logMfrUrl ||
        '';
    setIfMissing('MANUFACTURER URL 1', mfrUrl);

    // ── Manufacturer 2 & 3 ──
    if (mfrs[1]) {
        setIfMissing('MANUFACTURER NAME 2', mfrs[1].name);
        setIfMissing('MANUFACTURER URL 2', mfrs[1].url);
        setIfMissing('MANUFACTURER PART NUMBER 2', mfrs[1].partNumber);
    }
    if (mfrs[2]) {
        setIfMissing('MANUFACTURER NAME 3', mfrs[2].name);
        setIfMissing('MANUFACTURER URL 3', mfrs[2].url);
        setIfMissing('MANUFACTURER PART NUMBER 3', mfrs[2].partNumber);
    }

    // ── Vendor / Supplier Name ──
    const vdr1Name =
        vdrs[0]?.name ||
        rowData.givenSupplierName ||
        rowData.supplierName ||
        '';
    setIfMissing('VENDOR NAME', vdr1Name);

    // ── Vendor / Supplier Part Number ──
    const vdr1PN =
        vdrs[0]?.partNumber ||
        rowData.givenSupplierPartnumber ||
        rowData.supplierPartNumber ||
        '';
    setIfMissing('VENDOR PART NUMBER', vdr1PN);

    // ── Vendor URL 1 ──
    const vendorUrl =
        vdrs[0]?.url ||
        rowData.vendorUrl ||
        '';
    setIfMissing('VENDOR URL', vendorUrl);

    // ── Vendor 2–5 ──
    for (let i = 1; i < Math.min(vdrs.length, 5); i++) {
        setIfMissing(`VENDOR NAME ${i + 1}`, vdrs[i]?.name);
        setIfMissing(`VENDOR URL ${i + 1}`, vdrs[i]?.url);
        setIfMissing(`VENDOR PART NUMBER ${i + 1}`, vdrs[i]?.partNumber);
    }

    // ── sourceUrl-based fallbacks ──
    // If we STILL have no vendor name but have a sourceUrl, extract from domain
    const srcUrl = rowData.sourceUrl || logBestUrl || '';
    if (!merged['VENDOR NAME'] && srcUrl) {
        // sourceUrl is typically a vendor/distributor page (e.g., motion.com, grainger.com)
        const domainName = extractNameFromUrl(srcUrl);
        if (domainName) {
            merged['VENDOR NAME'] = domainName;
        }
    }
    if (!merged['VENDOR URL'] && srcUrl && !merged['MANUFACTURER URL 1']) {
        // If no manufacturer URL set, sourceUrl is likely vendor
        merged['VENDOR URL'] = srcUrl;
    }

    // ── sourceUrl as manufacturer URL fallback ──
    // Only if there were classified manufacturers and the sourceUrl domain matches
    if (!merged['MANUFACTURER URL 1'] && srcUrl && mfrs.length > 0) {
        merged['MANUFACTURER URL 1'] = srcUrl;
    }

    // ── Noun/Modifier ──
    setIfMissing('NOUN/MODIFIER',
        rowData.nounModifier ||
        ''
    );

    return merged;
};
